import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyH2,
  TypographyP,
  TypographyP2,
} from '@heidi/design-system/typography';
import { CircleCheck } from 'lucide-react';
import { useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';

const BAR_MAX_HEIGHTS = [10, 16, 12, 18, 8];

/** Live audio level meter shown while recording with the user's own voice. */
export function AudioBars({ level }: { level: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {BAR_MAX_HEIGHTS.map((maxH, i) => (
        <div
          key={i}
          className="w-1 rounded-full bg-validation-success"
          style={{
            height: `${Math.max(4, maxH * Math.min(level * 4, 1))}px`,
            transition: 'height 80ms ease-out',
          }}
        />
      ))}
    </div>
  );
}

/**
 * Terminal state of every mic-test variant: a confirmation and a single CTA.
 * `min-h-0 flex-1` rather than `h-full` because the personalization column is
 * height-bounded for `mic-test` — `h-full` plus the `gap` overflowed it and
 * clipped the CTA.
 */
export function MicTestReadyState({
  title,
  subtitle,
  ctaLabel,
  onContinue,
}: {
  title: string;
  subtitle: string;
  ctaLabel: string;
  onContinue: () => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-10">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <CircleCheck className="size-8 text-validation-success" />
          <TypographyH2 className="text-text-primary">{title}</TypographyH2>
        </div>
        <TypographyP className="text-text-secondary">{subtitle}</TypographyP>
      </div>

      <div className="flex flex-1 items-end pt-4">
        <Button size="lg" onClick={onContinue} className="w-full">
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
}

const LISTEN_BAR_HEIGHTS = [10, 16, 20, 13, 9];

/**
 * "Transcribing / Transcribed" status shown in the mic-test preview header for
 * the live-transcription treatment. While `active` the bars oscillate like an
 * equaliser (forest); once transcription settles they go static/tertiary.
 * Animates scaleY only (compositor-friendly) and respects reduced motion.
 */
export function MicListeningStatus({
  active,
  label,
}: {
  active: boolean;
  label: string;
}) {
  const reducedMotion = useReducedMotion();
  const animate = active && !reducedMotion;
  return (
    <div className="flex items-center gap-2">
      <span className="flex h-5 items-center gap-[3px]" aria-hidden>
        {LISTEN_BAR_HEIGHTS.map((h, i) => (
          <m.span
            key={i}
            className={`w-[3px] rounded-full ${
              active ? 'bg-accent-forest' : 'bg-text-tertiary'
            }`}
            style={{ height: `${h}px`, transformOrigin: 'center' }}
            animate={animate ? { scaleY: [0.35, 1, 0.35] } : { scaleY: 0.5 }}
            transition={
              animate
                ? {
                    duration: 0.8,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: i * 0.12,
                  }
                : { duration: 0.3 }
            }
          />
        ))}
      </span>
      <TypographyP2
        className={active ? 'font-medium text-accent-forest' : 'text-text-tertiary'}
      >
        {label}
      </TypographyP2>
    </div>
  );
}

const PLAYBACK_BARS = [14, 22, 10, 18, 12, 24, 16];

/**
 * Decorative equalizer shown while the sample conversation is playing back.
 * Animates opacity only (compositor-friendly).
 */
export function PlaybackBars() {
  return (
    <div className="flex items-end gap-0.5" aria-hidden>
      {PLAYBACK_BARS.map((h, i) => (
        <div
          key={i}
          className="w-1 animate-pulse rounded-full bg-text-primary-invert/90"
          style={{ height: `${h}px`, animationDelay: `${i * 90}ms` }}
        />
      ))}
    </div>
  );
}

/**
 * The "playing back the sample" card: equalizer badge, title, a time-remaining
 * caption and a progress bar so the user knows roughly how long is left.
 */
export function PlaybackCard({
  title,
  timeRemaining,
  remainingSeconds,
  progressPct,
}: {
  title: string;
  timeRemaining: string;
  remainingSeconds: number;
  progressPct: number;
}) {
  return (
    <div className="flex w-full flex-col gap-3 rounded-2xl border border-accent-forest/30 bg-accent-forest/[0.08] p-5">
      <div className="flex items-center gap-4">
        <div className="flex size-15 shrink-0 items-center justify-center rounded-full bg-accent-forest text-text-primary-invert shadow-[0_8px_20px_-8px_rgba(0,0,0,0.45)]">
          <PlaybackBars />
        </div>
        <div className="flex-1">
          <TypographyP2 className="text-base font-medium text-text-primary">
            {title}
          </TypographyP2>
          <TypographyCaption className="mt-0.5 text-text-tertiary">
            {timeRemaining}
          </TypographyCaption>
        </div>
        <TypographyCaption className="shrink-0 font-mono tabular-nums text-text-tertiary">
          {remainingSeconds}s
        </TypographyCaption>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-accent-forest/15"
        role="progressbar"
        aria-valuenow={progressPct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full bg-accent-forest transition-[width] duration-200 ease-linear"
          style={{ width: `${progressPct}%` }}
        />
      </div>
    </div>
  );
}
